import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, X, TrendingUp, Clock, DollarSign } from 'lucide-react';
import { createPortal } from 'react-dom';
import { IconVelocity, IconCapacity, IconLeverage, IconRisk, IconTension } from '../utils/PabsIcons';

const CARDS = [
  {
    id: 'solo',
    icon: IconVelocity,
    label: 'Solo Founder Impact',
    title: 'Development Cost Compression',
    stat: '$500–$5k',
    statLabel: 'Real AI-Assisted Cost',
    subStat: 'vs $30k–$120k traditional MVP',
    content: [
      { text: '10–20% engineering efficiency', source: 'Reuters, JPMorgan 2025', url: 'https://www.investing.com/news/stock-market-news/jpmorgan-engineers-efficiency-jumps-as-much-as-20-from-using-coding-assistant-3927898' },
      { text: '30–50% faster prototyping', source: 'McKinsey, Jun 2023', url: 'https://www.mckinsey.com/capabilities/tech-and-ai/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier' },
      { text: '25% faster knowledge work', source: 'HBS + BCG, Sep 2023', url: 'https://www.hbs.edu/faculty/Pages/item.aspx?num=64700' },
    ],
    highlight: 'Run 2–3× more experiments per year.',
  },
  {
    id: 'team',
    icon: IconCapacity,
    label: 'Small Team (5-10)',
    title: 'Capacity Shift',
    stat: '+$200k',
    statLabel: 'Annual Output Increase',
    subStat: 'Equivalent to 1 extra full-time engineer',
    content: [
      { text: '55.8% faster task completion', source: 'GitHub + Microsoft Research', url: 'https://github.blog/news-insights/research/research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/' },
      { text: '20–30% faster roadmap execution', source: 'Internal Data', url: null },
      { text: '1 extra release cycle per year', source: 'Without hiring', url: null },
    ],
    highlight: 'This does not mean layoffs. It means capacity shift.',
  },
  {
    id: 'enterprise',
    icon: IconLeverage,
    label: 'Enterprise (50 Eng.)',
    title: 'Capital Leverage',
    stat: '$2.1M',
    statLabel: 'Productivity Capacity',
    subStat: 'Based on $8.5M annual eng. cost',
    content: [
      { text: '10–20% efficiency gains', source: 'McKinsey Technology, 2024', url: 'https://www.mckinsey.com/industries/technology-media-and-telecommunications/our-insights/unlocking-the-value-of-ai-in-software-development' },
      { text: '25% task acceleration', source: 'HBS / BCG, Sep 2023', url: 'https://www.hbs.edu/faculty/Pages/item.aspx?num=64700' },
      { text: '30–50% reduced prototype cost', source: 'McKinsey, Apr 2024', url: 'https://www.mckinsey.com/capabilities/tech-and-ai/our-insights/unleashing-developer-productivity-with-generative-ai' },
    ],
    highlight: 'At this scale, AI is not a tool. It is capital leverage.',
  },
];

// Stacking Card — v6 API: scale via useTransform, no useMotionValueEvent needed here
function StackingCard({ card, index, totalCards, scrollYProgress }) {
  const step = 1 / totalCards;
  const start = index * step;
  const end = start + step;

  const scale = useTransform(scrollYProgress, [end - 0.05, end + 0.15], [1, 0.95]);
  const zIndex = 10 + index * 10;

  return (
    <motion.div
      style={{ scale, zIndex }}
      className="flex items-center mb-6 origin-top lg:sticky lg:top-24 lg:min-h-[calc(100vh-12rem)] lg:mb-24"
    >
      <div className="w-full group relative p-6 md:p-12 rounded-2xl md:rounded-[2rem] bg-brand-700 border border-brand-200/10 shadow-2xl overflow-hidden flex flex-col justify-between backdrop-blur-xl">
        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
          <card.icon className="w-32 h-32 text-brand-50" strokeWidth={1} />
        </div>

        <div>
          <div className="mb-6 flex items-center gap-3">
            <div className="text-xs font-bold text-brand-400 uppercase tracking-widest border border-brand-200/10 px-3 py-1 rounded-full bg-brand-50/5">
              {card.label}
            </div>
          </div>

          <h3 className="text-2xl md:text-3xl font-medium text-brand-50 mb-8">{card.title}</h3>

          <div className="grid grid-cols-1 gap-6 mb-8">
            <div className="bg-brand-50/5 rounded-xl p-6 border border-brand-50/5">
              <p className="text-xs text-brand-500 uppercase mb-2">{card.statLabel}</p>
              <div className="flex items-baseline gap-3 mb-1">
                <p className="text-4xl md:text-5xl font-bold text-brand-50 tracking-tight">
                  {card.stat}
                </p>
              </div>
              <p className="text-sm text-brand-400 font-mono">{card.subStat}</p>
            </div>
          </div>

          <div className="space-y-4">
            {card.content.map((item, i) => (
              <div
                key={i}
                className="flex justify-between items-start text-sm border-b border-brand-50/5 pb-3 last:border-0"
              >
                <span className="text-brand-300 font-medium max-w-[60%]">{item.text}</span>
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={item.source}
                    className="relative group/src ml-4 shrink-0 max-w-[120px] text-right"
                  >
                    <span className="text-brand-500 text-xs opacity-70 underline underline-offset-2 decoration-dotted decoration-brand-600 group-hover/src:opacity-100 group-hover/src:text-brand-300 transition-all duration-200">
                      {item.source}
                    </span>
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-max max-w-[200px] rounded-lg bg-brand-800 border border-brand-200/10 px-3 py-1.5 text-xs text-brand-200 opacity-0 group-hover/src:opacity-100 transition-opacity duration-200 shadow-xl whitespace-normal text-left z-50">
                      View source ↗
                    </span>
                  </a>
                ) : (
                  <span className="text-brand-500 text-xs text-right ml-4 shrink-0 max-w-[120px] opacity-70">
                    {item.source}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-brand-200/10">
          <div className="flex items-start gap-3">
            <div className="p-1.5 bg-brand-50/10 rounded-full mt-0.5">
              <TrendingUp className="w-4 h-4 text-brand-50" />
            </div>
            <span className="text-brand-300 text-sm leading-relaxed block italic">
              "{card.highlight}"
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function TitleText({ activeSection }) {
  return (
    <div className="space-y-8">
      <motion.div
        key={activeSection}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        {activeSection === 'acceleration' ? (
          <>
            <h2 className="text-5xl xl:text-6xl 2xl:text-8xl font-medium leading-[0.9]">
              The Age of <br />
              <span className="text-brand-600">Acceleration.</span>
            </h2>
            <div className="space-y-6 max-w-lg pt-8">
              <p className="text-xl text-brand-400 font-light leading-relaxed">
                AI has compressed the cost of execution to near zero. Velocity is no longer a
                constraint.
              </p>
              <div className="flex gap-6 text-sm font-mono text-brand-500 pt-4 border-t border-brand-200/10">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>25% Faster</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  <span>50% Cheaper</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <h2 className="text-5xl xl:text-6xl 2xl:text-8xl font-medium leading-[0.9]">
            <span className="text-brand-600">Execution is cheap.</span>
            <br />
            <span className="text-brand-50">Decisions aren't.</span>
          </h2>
        )}
      </motion.div>
    </div>
  );
}

function Modal({ onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm cursor-pointer"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-brand-800 border border-brand-200/10 rounded-3xl shadow-2xl flex flex-col md:flex-row overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-20 p-2 text-brand-400 hover:text-white bg-black/50 hover:bg-black/80 rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="w-full md:w-1/3 p-8 md:p-12 border-b md:border-b-0 md:border-r border-brand-200/10 bg-brand-900/50">
          <div className="sticky top-0">
            <h3 className="text-2xl font-medium text-white mb-6 leading-tight">
              Economic Impact of Strategic Misalignment
            </h3>
            <p className="text-brand-400 mb-8 leading-relaxed">
              While AI accelerates execution, it compounds the cost of poor strategic decisions. We
              modeled conservative scenarios for 2024-2026.
            </p>
            <div className="p-4 rounded-xl bg-brand-50/5 border border-brand-200/10">
              <p className="text-sm font-medium text-white mb-2">The Hidden Tax:</p>
              <p className="text-sm text-brand-300">
                AI increases the economic impact of poor decisions by{' '}
                <span className="text-white font-bold">10–30% minimum</span>.
              </p>
            </div>
          </div>
        </div>

        <div className="w-full md:w-2/3 p-8 md:p-12 bg-brand-800">
          <div className="grid gap-8">
            {[
              {
                icon: IconVelocity,
                title: 'Solo Founder Risk',
                trap: '3 failed AI-assisted MVPs/year',
                trapNote: 'Faster builds = faster failures',
                cost: '$30k–$90k burned',
                costNote: 'Entire year iterating noise',
              },
              {
                icon: IconCapacity,
                title: 'Small Team Risk (5–10)',
                trap: 'Roadmap wrong for 6 months',
                trapNote: 'Velocity creates false confidence',
                cost: '~$480k wasted',
                costNote: '~$400k capacity + 20% acceleration',
              },
              {
                icon: IconLeverage,
                title: 'Enterprise Risk',
                trap: '25% misaligned work',
                trapNote: 'Scaling waste across 50 engineers',
                cost: '~$2.5M misallocated',
                costNote: 'AI compounds bad strategy',
              },
            ].map((row, i) => (
              <div key={i} className={i < 2 ? 'pb-8 border-b border-brand-200/10' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <row.icon className="w-5 h-5 text-white" />
                  <h4 className="text-lg font-bold text-white">{row.title}</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-brand-500 mb-1">The Trap</p>
                    <p className="text-brand-300">{row.trap}</p>
                    <p className="text-xs text-brand-500 mt-1">{row.trapNote}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-brand-500 mb-1">The Cost</p>
                    <p className="text-xl font-bold text-white">{row.cost}</p>
                    <p className="text-xs text-red-500 mt-1">{row.costNote}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

export default function StrategyManifesto() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef(null);
  const [activeSection, setActiveSection] = useState('acceleration');

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Polyfill for useMotionValueEvent (framer-motion v6 uses .onChange)
  useEffect(() => {
    const unsubscribe = scrollYProgress.onChange((latest) => {
      if (latest > 0.7) {
        setActiveSection('tension');
      } else {
        setActiveSection('acceleration');
      }
    });
    return unsubscribe;
  }, [scrollYProgress]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <section
      ref={containerRef}
      className="relative bg-brand-900 text-brand-50 pt-20 md:pt-24 pb-16 lg:pb-0 border-t border-brand-200/10"
    >
      <div className="absolute md:fixed inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 pointer-events-none"></div>

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-24">

          {/* Left Column: Stacking Cards */}
          <div className="w-full lg:w-1/2 flex flex-col relative pb-0 lg:pb-32">
            {CARDS.map((card, index) => (
              <StackingCard
                key={card.id}
                card={card}
                index={index}
                totalCards={CARDS.length + 1}
                scrollYProgress={scrollYProgress}
              />
            ))}

            {/* Card 4: The Tension */}
            <div className="z-40 flex items-center mb-0 pb-0 lg:sticky lg:top-24 lg:min-h-[calc(100vh-12rem)] lg:pb-20">
              <div className="w-full group relative p-6 md:p-12 rounded-2xl md:rounded-[2rem] bg-brand-50 text-brand-900 shadow-2xl overflow-hidden flex flex-col justify-between transition-transform duration-500 hover:-translate-y-2">
                <div className="absolute top-0 right-0 p-8 opacity-5 transition-opacity">
                  <IconRisk className="w-48 h-48 text-brand-900" strokeWidth={1.5} />
                </div>

                <div className="relative z-10 flex flex-col gap-8">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-brand-900 animate-pulse"></div>
                    <span className="text-xs font-bold text-brand-900 uppercase tracking-widest">
                      The Structural Shift
                    </span>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-brand-900 leading-[1.2] tracking-tight">
                    If execution is 30–70% faster and dramatically cheaper,{' '}
                    <span className="block mt-2 text-2xl md:text-4xl">
                      what does a <span className="italic">lack of strategy</span> cost?
                    </span>
                  </h3>

                  <div className="space-y-1">
                    <p className="text-lg text-brand-900 font-medium">AI multiplies output.</p>
                    <p className="text-lg text-brand-500 font-medium">
                      It doesn't multiply judgment.
                    </p>
                  </div>

                  <div className="space-y-3 border-t border-brand-900/10 pt-6">
                    <div className="flex items-start gap-3">
                      <span className="text-xs font-bold text-brand-900 bg-brand-900/10 px-2 py-1 rounded-md font-mono shrink-0 mt-0.5">
                        35–42%
                      </span>
                      <p className="text-sm text-brand-600 leading-relaxed">
                        of startups still fail due to lack of market need.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-xs font-bold text-brand-900 bg-brand-900/10 px-2 py-1 rounded-md font-mono shrink-0 mt-0.5">
                        80%
                      </span>
                      <p className="text-sm text-brand-600 leading-relaxed">
                        of shipped features still go unused.
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-brand-500 leading-relaxed border-l-2 border-brand-900/20 pl-4">
                    Speed without strategy isn't progress.<br />
                    it's expensive mistakes in real time.
                  </p>

                  <p className="text-xs font-bold text-brand-900 uppercase tracking-widest">
                    The New Future Bottleneck: Direction
                  </p>
                </div>

                <div className="mt-8 pt-8 border-t border-brand-200 relative z-10">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="group/btn w-full py-3.5 bg-transparent text-brand-900 border border-brand-900/30 rounded-xl text-sm font-medium hover:border-brand-900 hover:bg-brand-900/5 transition-all flex items-center justify-center gap-2"
                  >
                    See the Economic Impact
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Sticky Title (desktop) */}
          <div className="hidden lg:block lg:w-1/2 relative">
            <div className="sticky top-0 h-screen flex flex-col justify-center pl-12">
              <TitleText activeSection={activeSection} />
            </div>
          </div>

          {/* Mobile Title (static) */}
          <div className="lg:hidden col-span-1 mb-8">
            <h2 className="text-4xl font-bold tracking-tight leading-tight mb-6">
              <span className="text-brand-600">Execution is cheap.</span>
              <br />
              <span className="text-brand-50">Decisions aren't.</span>
            </h2>
            <p className="text-lg text-brand-400 font-light leading-relaxed">
              Speed without strategy isn't progress. It's just expensive mistakes in real-time.
            </p>
          </div>
        </div>
      </div>

      {isModalOpen && <Modal onClose={() => setIsModalOpen(false)} />}
    </section>
  );
}
